import { useEffect, useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { ListingService } from "@/services/listing/listingService";
import { sellerContractNeedsSignature } from "@/lib/sellerContractStatus";
import { useAuth } from "@/hooks/useAuth";
import { UserType } from "@/types/user";

const SEEN_STORAGE_KEY = "bilenia_myObjectsAttentionSeen_v1";

type SeenState = {
  sellerContractIds: string[];
  buyerContractIds: string[];
};

function readSeen(): SeenState {
  try {
    const raw = localStorage.getItem(SEEN_STORAGE_KEY);
    if (!raw) return { sellerContractIds: [], buyerContractIds: [] };
    const parsed = JSON.parse(raw) as SeenState;
    return {
      sellerContractIds: Array.isArray(parsed.sellerContractIds)
        ? parsed.sellerContractIds
        : [],
      buyerContractIds: Array.isArray(parsed.buyerContractIds)
        ? parsed.buyerContractIds
        : [],
    };
  } catch {
    return { sellerContractIds: [], buyerContractIds: [] };
  }
}

function writeSeen(next: SeenState) {
  try {
    localStorage.setItem(SEEN_STORAGE_KEY, JSON.stringify(next));
  } catch {
    /* ignore quota */
  }
}

/** Call when user opens Mina annonser → Avslutade (seller contract attention). */
export function markSellerAttentionSeen(contractIds: string[]) {
  if (contractIds.length === 0) return;
  const seen = readSeen();
  const set = new Set([...seen.sellerContractIds, ...contractIds]);
  writeSeen({ ...seen, sellerContractIds: [...set] });
  window.dispatchEvent(new Event("bilenia-attention-seen"));
}

/** Call when user opens Ordrar (buyer contract attention). */
export function markBuyerAttentionSeen(contractIds: string[]) {
  if (contractIds.length === 0) return;
  const seen = readSeen();
  const set = new Set([...seen.buyerContractIds, ...contractIds]);
  writeSeen({ ...seen, buyerContractIds: [...set] });
  window.dispatchEvent(new Event("bilenia-attention-seen"));
}

/**
 * Attention dots for dashboard "Mina objekt" + Ordrar / Mina annonser tabs.
 * Dots clear after the user has opened the relevant tab (localStorage).
 * Skipped for admins — my-orders is a customer endpoint and 401-storms the dashboard.
 */
export function useMyObjectsAttention() {
  const { user } = useAuth();
  const [seenTick, setSeenTick] = useState(0);
  const isCustomer =
    Boolean(user) && user!.type !== UserType.Admin;

  useEffect(() => {
    const onSeen = () => setSeenTick((n) => n + 1);
    window.addEventListener("bilenia-attention-seen", onSeen);
    return () => window.removeEventListener("bilenia-attention-seen", onSeen);
  }, []);

  const listingsQuery = useQuery({
    queryKey: ["listings", "my-listings", "attention", { status: "completed" }],
    queryFn: () =>
      ListingService.getMyListings({
        status: "completed",
        page: 1,
        limit: 50,
      }),
    enabled: isCustomer,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  const ordersQuery = useQuery({
    queryKey: ["listings", "my-orders", "attention"],
    queryFn: () =>
      ListingService.getMyOrders({
        page: 1,
        limit: 50,
      }),
    enabled: isCustomer,
    staleTime: 60_000,
    retry: false,
    refetchOnWindowFocus: false,
  });

  return useMemo(() => {
    const seen = readSeen();
    const listings = listingsQuery.data?.listings ?? [];
    const orders = ordersQuery.data?.orders ?? [];

    const pendingSellerContractIds = listings
      .filter((l) => sellerContractNeedsSignature(l))
      .map((l) => l.sellerContract?.id)
      .filter((id): id is string => Boolean(id));

    const pendingBuyerContractIds = orders
      .map((o) => o.listing?.buyer_payment?.contract)
      .filter(
        (c): c is NonNullable<typeof c> =>
          Boolean(c) && c!.buyer_signature_status === "requested",
      )
      .map((c) => c.id);

    const sellerNeedsAction = pendingSellerContractIds.some(
      (id) => !seen.sellerContractIds.includes(id),
    );
    const buyerNeedsAction = pendingBuyerContractIds.some(
      (id) => !seen.buyerContractIds.includes(id),
    );

    return {
      sellerNeedsAction,
      buyerNeedsAction,
      myObjectsNeedsAction: sellerNeedsAction || buyerNeedsAction,
      pendingSellerContractIds,
      pendingBuyerContractIds,
      isLoading: listingsQuery.isLoading || ordersQuery.isLoading,
    };
  }, [
    listingsQuery.data,
    ordersQuery.data,
    listingsQuery.isLoading,
    ordersQuery.isLoading,
    seenTick,
  ]);
}
